import { from as observableFrom, of as observableOf, Subscription } from 'rxjs';
import { Component, ViewEncapsulation, OnInit, OnDestroy, ElementRef, ViewChild, NgZone, ApplicationRef } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { ActivityViewHelperService } from './../../services/activity-view-helper.service';
import { AppHelperService } from './../../../app/services/app-helper.service';
import { LoaderService } from 'modules/app/services/loader.service';
import { ContentHelper } from 'libs-content-helper';
import { ThemeService, configService } from "libs-frontend-micro-base";
import { ContentAccessHelperService } from '../../services/content-access-helper.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'activity-launch',
    templateUrl: './activity-launch.template.html',
    styleUrls: ['./activity-launch.style.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class ActivityLaunch implements OnInit, OnDestroy {
    contentHelper = ContentHelper;
    paramsSubscription: Subscription;
    activityEventsSubscription: Subscription;
    progressBarSubscription: Subscription;
    itemid: string;
    toc: any;
    selectedItemData: any;
    selectedItem: any = {};
    bookPath: string;
    productMeta: any;
    classMeta: any;
    saveAlertNeeded: boolean = false;
    productCode: string;
    currentScreenNumber: number;
    showProgressBar: boolean = false;
    totalScreenCount: number;
    folderId: any;
    themeService: any;
    unlockDetail: any = {};
    classEndDate: number;
    isCurrentActivityLocked: boolean = false;
    showLockScreenLoader: boolean = false;
    /* segment primary or secondary*/
    segment: string = '';
    @ViewChild('showSaveAlertModalBtn') showSaveAlertModalBtn: ElementRef;
    productTOCData: any;
    itemId: any;
    config;
    lessonFeatureKey: any = "";

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public activityViewHelperService: ActivityViewHelperService,
        private appHelperService: AppHelperService,
        private loaderService: LoaderService,
        zone: NgZone,
        applicationRef: ApplicationRef,
        private readonly contentAccessHelperService: ContentAccessHelperService,
        private translate: TranslateService) {
        this.themeService = new ThemeService();
        const userAgent = window.navigator && window.navigator.userAgent && window.navigator.userAgent.toLowerCase();
        if ((userAgent.includes('firefox') || userAgent.includes('safari') || userAgent.includes('ipad')) && !userAgent.includes('chrome')) {
            router.events.subscribe(() => {
                zone.run(() =>
                    setTimeout(() => {
                        applicationRef.tick();
                    })
                );
            });
        }
    }
    ngOnInit() {
        this.productTOCData = this.route.parent.snapshot.data.activityViewResolverData.productData;
        const userRole = this.route.parent.snapshot.data.activityViewResolverData.userProfileData.role;
        this.toc = this.productTOCData.toc;
        this.productMeta = this.productTOCData.productDetails.meta;
        this.classMeta = this.productTOCData.classDetails;
        this.bookPath = this.productTOCData.productDetails.meta.paths['public-assets'];

        if (this.productTOCData.classDetails) {
            this.classEndDate = this.productTOCData.classDetails.class.enddate;
        }

        //subscribe to parent route changes to capture change in productCode
        this.route.parent.params.subscribe((params: Params) => {
            this.productCode = params.productCode;
            this.folderId = params.folderId;
        });

        //subscribe to route changes to capture change in itemid
        //and launch new activity
        this.paramsSubscription = this.route.params.subscribe((params: Params) => {
            this.saveAlertNeeded = false;
            // Navigate to first item, if current itemId is not found in the product
            if (this.toc.navigationMeta.find(item => item['item-code'] === params.itemId) === undefined) {
                const requiredQueryParams = this.appHelperService.getRequiredQueryParams(this.route.snapshot.queryParams, ['lf', 'cid', 'bcode', 'rcode', 'ltik', 'b']);
                return this.router.navigate(['../../item', this.toc.navigationMeta[0]['item-code']], { relativeTo: this.route, replaceUrl: true, queryParams: requiredQueryParams });
            }
            this.setSelectedItem(params.itemId);
            setTimeout(() => {
                this.activateSubTypeExt().catch((e) => { throw e; });
            });
        });

        //Get segment style - to display corresponding result screen accordingly
        const classProduct = this.productTOCData.bundleDetails.products.find((product) => product['dls-product'].code === this.productCode);
        this.segment = classProduct.segment ? classProduct.segment.segment : '';
        this.themeService.activateThemeOnComponent(userRole === 'student' && this.segment ? this.segment : 'adult', 'activityLaunch');
        this.activityViewHelperService.setSegment(this.segment);
        //Recording alert activity closed case
        // Activity Events for SPA
        this.activityEventsSubscription = this.activityViewHelperService.activityEventsSubject.subscribe(({ containerEvent, containerEventData }) => {
            switch (containerEvent) {
                case 'currentScreen': {
                    this.currentScreenNumber = containerEventData;
                    return;
                }
                case 'container_controls': {
                    if (containerEventData === 'screenLaunched') {
                        this.showProgressBar = true;
                    }
                    return;
                }
                case 'activityClosed': {
                    this.showProgressBar = false;
                    return;
                }
                case 'totalScreens': {
                    this.totalScreenCount = containerEventData;
                    return;
                }
            }
        });

        // Event to show progress bar for pskill, collab and downloadable activities
        this.progressBarSubscription = this.activityViewHelperService.progressBarSubject.subscribe(data => {
            this.toggleProgressBar(data);
        });
    }

    private toggleProgressBar(data) {
        // Added setTimeout to fix "Expression has changed after it was checked" error
        setTimeout(() => {
            this.showProgressBar = data.visible;
            this.currentScreenNumber = data.currentScreen ? data.currentScreen : 1;
            this.totalScreenCount = data.totalScreen ? data.totalScreen : 1;
        });
    }

    private setSelectedItem(itemid) {
        if (this.activityViewHelperService.existsActivityTiming(this.selectedItem.activityId)) {
            this.activityViewHelperService.addActivityTiming(this.selectedItem.activityId, 'nextItem');
        }

        this.itemid = decodeURIComponent(itemid);
        let decodedParamsArray = this.itemid.split('/');
        // in case folderId is present, will need to prepend folderCode to unit, lesson and item
        if (this.folderId) {
            this.selectedItem = {
                ...(decodedParamsArray[0] && { unitId: decodedParamsArray[0] + '/' + decodedParamsArray[1] }),
                ...(decodedParamsArray[1] && { lessonId: decodedParamsArray[0] + '/' + decodedParamsArray[1] + '/' + decodedParamsArray[2] }),
                ...(decodedParamsArray[2] && { activityId: decodedParamsArray[0] + '/' + decodedParamsArray[1] + '/' + decodedParamsArray[2] + '/' + decodedParamsArray[3] }),
            };

        } else {
            this.selectedItem = {
                ...(decodedParamsArray[0] && { unitId: decodedParamsArray[0] }),
                ...(decodedParamsArray[1] && { lessonId: decodedParamsArray[0] + '/' + decodedParamsArray[1] }),
                ...(decodedParamsArray[2] && { activityId: decodedParamsArray[0] + '/' + decodedParamsArray[1] + '/' + decodedParamsArray[2] }),
            };

        }

        this.setSelectedItemData();
        this.activityViewHelperService.selectedItemId.next({
            itemid: this.itemid,
            selectedItem: this.selectedItem,
            selectedItemData: this.selectedItemData
        });

    }

    private setSelectedItemData() {
        // Fixed issue coming when back button pressed from dashboard page to directly activity launch page
        if (!this.selectedItem || !this.selectedItem.unitId || !this.selectedItem.lessonId || !this.selectedItem.activityId) {
            return;
        }

        this.selectedItemData = this.toc.tocObject[this.selectedItem.unitId].items[this.selectedItem.lessonId].items[this.selectedItem.activityId];
        if (this.contentHelper.isDownloadableContent(this.selectedItemData)) {
            var subType = this.selectedItemData["sub-type"];
            var downloadUrl = this.appHelperService.isAbsoluteUrl(this.selectedItemData[subType].url) ? this.selectedItemData[subType].url : (this.bookPath + '/' + this.selectedItemData[subType].url);
            this.selectedItemData.downloadUrl = downloadUrl;
            this.selectedItemData.filename = this.selectedItemData[subType].filename;
        } else if (["cup-pskill", "cup-pskill-group", "cup-collab-task"].indexOf(this.selectedItemData["sub-type"]) != -1) {
            //productive-skill component auto handles
            //add itemid to 'pskill' input to be used in productive-skill component
            this.selectedItemData[this.selectedItemData["sub-type"]]['cup-options'].itemid = this.itemid;
            //this.selectedItemData[]
        }
    }

    private async activateSubTypeExt() {
        const itemData = this.selectedItemData;

        if (this.contentAccessHelperService.getContentAccessEnabled()) {
            const previousActivityState = itemData['accessInfo'].isLocked;

            const activeRule = this.contentAccessHelperService.getCurrentActiveRule(itemData.accessInfo.finalResultantLockDetails, itemData['item-code'], this.activityViewHelperService.getIsAssignmentOnlyProduct());
            itemData['accessInfo']['isLocked'] = activeRule.isLocked;
            this.unlockDetail = activeRule.unlockRuleDetails;

            // State change means increment or decrement child lock count
            if (itemData['accessInfo'].isLocked !== previousActivityState) {
                this.activityViewHelperService.updateParentChildLockCount(this.toc, itemData);
            }

            if (itemData['accessInfo'].isLocked) {
                this.isCurrentActivityLocked = true;
                this.showLockScreenLoader = true;
                setTimeout(() => {
                    this.showLockScreenLoader = false;
                }, 100);
                this.loaderService.hideLoader();
                return;
            }
        }

        this.isCurrentActivityLocked = false;
        const contentPath = this.bookPath + '/' + itemData[itemData["sub-type"]]['url'];
        const contentType = itemData["sub-type"];
        const initParams: any = {
            learningObject: {
                path: contentPath,
                contentType: contentType,
                code: itemData['item-code'],
                name: itemData['item-code'],
                meta: { contributesToCompletion: itemData['contributes-to-completion'] },
                iframeTitle: this.translate.instant("SHARED.ACTIVITY_RENDERER.SCREEN_READER.ACTIVITY", {activityName: itemData['name']})
            }
        };
        if (contentType === "ext-cup-xapi" || contentType === "ext-cup-xapiscoreable") {
            var containerType = 'avallain-opener';
            if (itemData[contentType]['container'] === 'Channel') {
                containerType = 'generic-content';
            } else if (itemData[contentType]['container'] === 'spa-container') {
                containerType = 'spa-container';
            }
            initParams.container = {
                type: containerType,
                config: {
                    disableLOButtons: true,
                }
            };
            // Enable Walkthrough for spa-container
            if (containerType === 'spa-container') initParams.container.config.enableWalkthrough = true;

            //param to show c1 result screen for generic scorable
            if (ContentHelper.containerResultScreen(itemData)) {
                initParams.container.config.loExternalResultScreen = true;
            }

            if (containerType === 'avallain-opener' || containerType === 'generic-content') {
                if (itemData[contentType]["cup-options"]) {
                    initParams.container.config.paths = {
                        engine: itemData[contentType]["cup-options"].engine,
                        dp: itemData[contentType]["cup-options"].dp
                    }
                }
            }

            // __analytics node won't exist for teacher hence we won't call getState for teacher
          if (itemData.__analytics && itemData.__analytics.status === "in_complete") {
              const response = await this.activityViewHelperService.getXAPIAnalyticsObject().getState(itemData['item-code']);
              initParams.learningObject.state = response;
            } else {
                initParams.learningObject.status = "completed"; // By default set status as "completed" (for teachers)
                if (itemData.__analytics) {
                    // For learner, pass the value of status from analytics node
                    initParams.learningObject.status = itemData.__analytics.status;
                }
            }
            this.activityViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
        } else if (contentType === "ext-cup-html") {
            this.activityViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
        } else if (this.contentHelper.isDownloadableContent(itemData)) {
            initParams.container = {
                type: 'nopack-content',
                config: {
                    trackLaunch: true,
                    trackClose: true,
                    trackTime: false,
                    trackComplete: true
                }
            }
            this.activityViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
            this.toggleProgressBar({ visible: true });
        } else if (["cup-pskill", "cup-pskill-group", "cup-collab-task"].indexOf(contentType) != -1) {
            initParams.container = {
                type: 'nopack-content',
                config: {
                    trackLaunch: contentType == "cup-collab-task" ? true : false,
                    trackClose: true,
                    trackTime: true,
                    trackComplete: false
                }
            }
            this.activityViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
        }
        this.activityViewHelperService.setLOInitParams(initParams);
        this.loaderService.hideLoader();
    }

    downloadActivity() {
        this.activityViewHelperService.getXAPIRunnerObject().generateCustomStatement(this.itemid, "downloaded");
    }

    public canDeactivate() {
        const itemData = this.selectedItemData;
        // Assume that the activity needs to be closed
        let returnObservable = observableOf(true);

        if (!this.activityViewHelperService.getIsTeacher() && itemData &&
            ContentHelper.isSubmittableContent(itemData) && this.saveAlertNeeded) {
            // Show save alert
            this.loaderService.hideLoader();
            this.showSaveAlertModalBtn.nativeElement.click();
            // Save alert component will generate true if the user wants to leave the activity
            returnObservable = this.activityViewHelperService.saveAlertAction;
        }

        return returnObservable.pipe(
            switchMap(closeActivity => {
                // Close the activity if the user wants to leave the activity
                if (closeActivity) {
                    if (itemData && itemData.accessInfo && !itemData['accessInfo'].isLocked) {
                        return observableFrom<any>(this.activityViewHelperService.getXAPIRunnerObject().closeLO(itemData['item-code']).then(() => {
                            if (this.contentHelper.isDownloadableContent(itemData)) {
                                this.toggleProgressBar({ visible: false });
                            }
                            return true;
                        }));
                    }
                }
                return observableOf(closeActivity);
            })
        );
    }

    isSaveAlertNeeded(val) {
        this.saveAlertNeeded = val;
    }

    ngOnDestroy() {
        if (this.paramsSubscription) {
            this.paramsSubscription.unsubscribe();
        }
        if (this.activityEventsSubscription) {
            this.activityEventsSubscription.unsubscribe();
        }
        if (this.progressBarSubscription) {
            this.progressBarSubscription.unsubscribe();
        }
    }
}
